import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Logo from "./sub-components/Logo";
import MobileLangCurrChange from "./sub-components/MobileLangCurrChange";
import LinkWrapper from "../linkWrapper/LinkWrapper";
import Image from "../image/Image";
import { navChildNames } from "./utils";
import { constructArtificialLinkNode, getStaticPageLink } from "../../../utils/links";
import { getImageLink } from "../../../utils/images";
import { decodeText } from "../../../utils/richText";

const NavItem = ({ node, level }) => {
    const [expanded, setExpanded] = useState(false);
    const label = decodeText(node.link && node.link.label);
    const hasChildren = navChildNames(node).length > 0;

    if (!label) return null;

    return (
        <li className={`shop-offcanvas__item ${expanded ? "shop-offcanvas__item--open" : ""}`}>
            {hasChildren ? (
                <button
                    type="button"
                    className="shop-offcanvas__item-row shop-offcanvas__item-row--button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((value) => !value)}
                >
                    <span className="shop-offcanvas__link">{label}</span>
                    <i className={`fa ${expanded ? "fa-angle-up" : "fa-angle-down"}`} />
                </button>
            ) : (
                <div className="shop-offcanvas__item-row">
                    <LinkWrapper linkNode={node.link} renderChildren fallbackWrapperEl="span" linkClass="shop-offcanvas__link">
                        {label}
                    </LinkWrapper>
                </div>
            )}
            {hasChildren && expanded && <NavTree node={node} level={level + 1} />}
        </li>
    );
};

const NavTree = ({ node, level = 0 }) => (
    <ul className={`shop-offcanvas__list shop-offcanvas__list--level-${level}`}>
        {navChildNames(node).map((name) => (
            <NavItem key={name} node={node[name]} level={level} />
        ))}
    </ul>
);

// Full-height off-canvas menu for small screens: the shop navigation as an accordion, the
// account links as a 2x2 grid, the trust items ("Why shop with us") on a grey card, then
// language, currency and contact. Every followed link closes the menu.
const MobileOffCanvas = ({
    open,
    onClose,
    content = {},
    trustItems = [],
    trustTitle = "Why shop with us",
    phone,
    email,
    settings = { showCompare: true, showWishlist: true },
}) => {
    useEffect(() => {
        document.body.classList.toggle("shop-offcanvas-open", open);
        return () => document.body.classList.remove("shop-offcanvas-open");
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const closeOnEscape = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [open, onClose]);

    const closeOnLinkClick = (e) => {
        if (e.target.closest("a")) onClose();
    };

    const accountLinks = [
        ["Account", "LOGIN_REGISTER"],
        settings.showCompare && ["Compare", "COMPARE"],
        settings.showWishlist && ["Wishlist", "WISHLIST"],
        ["Cart", "CART"],
    ].filter(Boolean);

    return (
        <div className={`shop-offcanvas ${open ? "shop-offcanvas--open" : ""}`} aria-hidden={!open}>
            <div className="shop-offcanvas__backdrop" onClick={onClose} />
            <aside className="shop-offcanvas__panel" role="dialog" aria-modal="true" aria-label="Menu">
                <div className="shop-offcanvas__head">
                    <Logo logoClass="shop-offcanvas__logo" />
                    <button type="button" className="shop-offcanvas__close" onClick={onClose} aria-label="Close menu">
                        <i className="pe-7s-close" />
                    </button>
                </div>

                <div className="shop-offcanvas__body" onClick={closeOnLinkClick}>
                    <nav className="shop-offcanvas__nav" aria-label="Shop">
                        <NavTree node={content} />
                    </nav>

                    <hr className="shop-offcanvas__divider" />

                    <ul className="shop-offcanvas__account">
                        {accountLinks.map(([label, pageType]) => (
                            <li key={pageType}>
                                <LinkWrapper linkNode={constructArtificialLinkNode({ value: getStaticPageLink(pageType) })} linkClass="shop-offcanvas__account-link">
                                    {label}
                                </LinkWrapper>
                            </li>
                        ))}
                    </ul>

                    {trustItems.length > 0 && (
                        <div className="shop-offcanvas__trust">
                            <h4>{trustTitle}</h4>
                            <ul>
                                {trustItems.slice(0, 4).map((item, idx) => (
                                    <li key={idx}>
                                        {getImageLink(item.image) && <Image imageNode={item.image} className="shop-offcanvas__trust-icon" />}
                                        <span>{decodeText(item.title)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="shop-offcanvas__meta">
                        <MobileLangCurrChange onChange={onClose} />
                        {(phone || email) && (
                            <ul className="shop-offcanvas__contact">
                                {phone && (
                                    <li>
                                        <i className="fa fa-phone" /> <a href={`tel:${phone.replace(/\s+/g, "")}`}>{phone}</a>
                                    </li>
                                )}
                                {email && (
                                    <li>
                                        <i className="fa fa-envelope" /> <a href={`mailto:${email}`}>{email}</a>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </aside>
        </div>
    );
};

MobileOffCanvas.propTypes = {
    content: PropTypes.object,
    email: PropTypes.string,
    onClose: PropTypes.func,
    open: PropTypes.bool,
    phone: PropTypes.string,
    settings: PropTypes.object,
    trustItems: PropTypes.array,
    trustTitle: PropTypes.string,
};

export default MobileOffCanvas;